import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUp } from "lucide-react";

const SHOW_AFTER_PX = 300;

/**
 * Resets scroll on route change and renders the floating scroll-to-top button.
 * The button is pinned to the LEFT edge so it can never collide with the chat
 * bubble or any embedded widget, which all sit bottom-right.
 */
const ScrollToTop = () => {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > SHOW_AFTER_PX);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <AnimatePresence>
      {visible && (
        <motion.button
          key="scroll-top"
          initial={{ opacity: 0, x: -16, scale: 0.85 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          exit={{ opacity: 0, x: -16, scale: 0.85 }}
          transition={{ duration: 0.28, ease: "easeOut" }}
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label="Scroll back to top"
          className="fixed bottom-5 left-4 sm:bottom-7 sm:left-6 z-[70] h-11 w-11 sm:h-12 sm:w-12 rounded-full bg-primary text-primary-foreground border border-white/25 backdrop-blur flex items-center justify-center shadow-[0_10px_28px_-8px_hsl(var(--primary)/0.65)] hover:scale-110 hover:shadow-[0_14px_34px_-8px_hsl(var(--primary)/0.75)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 transition-all duration-300"
        >
          <ArrowUp className="h-5 w-5" />
        </motion.button>
      )}
    </AnimatePresence>
  );
};

export default ScrollToTop;
